import {
  doc, runTransaction, setDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase.js';

const COLLECTION = 'idempotency_keys';

export function createIdempotencyKey(prefix = 'op') {
  return `${prefix}_${crypto.randomUUID()}`;
}

function stableStringify(value) {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
}

function docIdFor(uid, operation, key) {
  return encodeURIComponent(`${uid}:${operation}:${key}`);
}

function payloadHash(payload) {
  let hash = 2166136261;
  const text = stableStringify(payload || {});
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16);
}

export async function runClientIdempotent({ uid, operation, key, payload, handler }) {
  const idempotencyKey = key || createIdempotencyKey(operation);
  const hash = payloadHash(payload);
  const ref = doc(db, COLLECTION, docIdFor(uid, operation, idempotencyKey));
  let existing = null;

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      tx.set(ref, {
        uid,
        operation,
        key: idempotencyKey,
        payloadHash: hash,
        status: 'processing',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return;
    }
    existing = snap.data();
  });

  if (existing) {
    if (existing.payloadHash !== hash) throw new Error('This action was already submitted with different data.');
    if (existing.status === 'completed') return existing.result;
    if (existing.status === 'failed') throw new Error(existing.errorMessage || 'The previous attempt failed.');
    throw new Error('This action is already being processed. Retry shortly.');
  }

  try {
    const result = await handler(idempotencyKey);
    await setDoc(ref, {
      status: 'completed',
      result,
      updatedAt: serverTimestamp(),
      completedAt: serverTimestamp(),
    }, { merge: true });
    return result;
  } catch (err) {
    await setDoc(ref, {
      status: 'failed',
      errorMessage: err.message || 'Action failed.',
      updatedAt: serverTimestamp(),
      completedAt: serverTimestamp(),
    }, { merge: true }).catch(() => {});
    throw err;
  }
}
